import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import EnquiryForm from "./EnquiryForm";

export default function HeroSection() {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <>
      <section
        className="
          relative min-h-screen overflow-hidden
          bg-[radial-gradient(1200px_at_10%_-10%,#374151,transparent_40%),radial-gradient(900px_at_90%_10%,#1f2937,transparent_45%),linear-gradient(135deg,#111827,#1f2937,#374151)]
          pt-16 sm:pt-20
        "
      >
        {/* ================= BACKGROUND GLOWS ================= */}
        <div className="absolute -top-40 -left-40 w-[420px] h-[420px] bg-yellow-400/12 rounded-full blur-[140px]" />
        <div className="absolute top-1/3 -right-40 w-[420px] h-[420px] bg-yellow-400/10 rounded-full blur-[160px]" />
        <div className="absolute inset-0 bg-black/30" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-2 sm:py-4 md:py-6 grid lg:grid-cols-2 gap-6 xl:gap-10 items-start">

          {/* ================= LEFT CONTENT ================= */}
          <div className="space-y-4 sm:space-y-6 text-white">

            {/* ========== CAPSULE/PILL SHAPED WHITE BORDER - CENTERED ========== */}
            <div className="flex justify-center">
              <div
                className="
                  inline-block
                  px-6 py-3 sm:px-8 sm:py-4
                  border-2 border-white
                  rounded-full
                  bg-white/5
                  backdrop-blur-sm
                  hover:bg-white/10
                  hover:shadow-[0_0_30px_rgba(255,255,255,0.3)]
                  transition-all duration-300
                "
              >
                <p className="text-lg sm:text-xl md:text-2xl xl:text-3xl font-bold tracking-wide text-white leading-relaxed text-center">
                  Extensive Experience and Specialized Expertise in
                </p>
              </div>
            </div>

            {/* MAIN HEADING */}
            <h1
              className="
                font-playfair font-extrabold leading-tight
                text-yellow-400
                text-4xl sm:text-5xl md:text-6xl xl:text-7xl
              "
            >
              ED, GST
              <br />
              Income Tax & Cyber Crime Matters
            </h1>

            {/* DESCRIPTION */}
            <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-xl leading-relaxed">
              Delivering precise legal representation in enforcement, taxation,
              and cyber offence matters with a strong focus on strategic defence,
              regulatory compliance, and client-first advocacy.
            </p>

            {/* ================= CTA BUTTONS ================= */}
            <div className="flex flex-col gap-2.5 sm:gap-3 pt-3 sm:pt-4 max-w-md">

              {/* PRACTICE AREAS BUTTON */}
              <Link
                to="/practice-areas"
                onClick={scrollToTop}
                className="w-full px-8 py-3 sm:py-4 border-2 border-yellow-400/60 text-yellow-400
                           font-semibold rounded-md hover:bg-yellow-400/10 text-center
                           hover:border-yellow-400 transition-all duration-300 text-base sm:text-lg"
              >
                Practice Areas
              </Link>

              {/* WHATSAPP & CALL BUTTONS */}
              <div className="grid grid-cols-2 gap-2.5 sm:gap-3">

                {/* WHATSAPP */}
                <a
                  href="https://wa.me/917303269472?text=Hello%2C%20I%20would%20like%20to%20inquire%20about%20legal%20services"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-3 sm:px-6 sm:py-4 
                             bg-[#25D366] text-white font-semibold rounded-md
                             hover:bg-[#128C7E] hover:shadow-lg hover:shadow-green-500/30
                             transition-all duration-300"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span className="text-sm sm:text-base">WhatsApp</span>
                </a>

                {/* CALL */}
                <a
                  href="tel:+917303269472"
                  className="flex items-center justify-center gap-2 px-4 py-3 sm:px-6 sm:py-4 
                             bg-yellow-400 text-gray-900 font-semibold rounded-md
                             hover:bg-yellow-500 hover:shadow-lg hover:shadow-yellow-400/30
                             transition-all duration-300"
                >
                  <Phone size={20} />
                  <span className="text-sm sm:text-base">Call Now</span>
                </a>

              </div>
            </div>
          </div>

          {/* ================= RIGHT FORM ================= */}
          <div className="w-full lg:flex lg:justify-end">
            <div
              className="
                w-screen lg:w-full
                lg:max-w-[500px]
                bg-white
                shadow-2xl shadow-yellow-400/10
                px-4 sm:px-6 py-6 sm:py-8 md:py-10
                -mx-4 sm:mx-0
                rounded-none lg:rounded-lg
              "
            >
              <EnquiryForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
